// The model's CANONICAL BLOCK — `GET /api/models/{modelId}/facts` and its two write routes.
//
// ⚠️ A THIRD MODULE BESIDE `modelApi.ts` AND `mdrApi.ts`, for the same reason the second one exists:
// a different route family on the same base. It shares their transport (`modelRegistryFetch.ts`) —
// one `ModelApiError`, one bounded abort, one ADR-043 decode — because `mdrApi.ts` already paid for
// the lesson that a second copy of the transport drifts (its `error_args` half was fixed on one side
// and not the other before B-3 folded them together).
//
// 🔴 ONE BLOCK IN THE UI, A PER-FACT AXIS IN THE DATA. NEITHER IS WRONG.
// This is the FE half of the pointer ruling `R-B` (2026-08-22) says is owed at the canonical block.
// The screen draws the canonical AS A WHOLE with one `Last updated` stamp and NO per-field review
// state — that is the ruling. The rows below still carry `review_status` per fact, because locked
// decision **L5**'s re-parse targeting is defined over it (*"a re-parse only touches rows still
// unreviewed"*) and `model_facts_unreviewed_idx` is the index that serves it. Strip the axis and a
// re-parse either overwrites a human's edits or targets nothing.
// ⛔ So: do NOT render `source`, `review_status` or `llm_io_ref` per row, and do NOT delete them from
// this type to match the screen. The canonical record of this decision is
// `Bennie.Api.Models.ModelFactReviewStatuses` in `apps/api`; this comment is the mirror.

import {
  ModelApiError,
  jsonOrThrow,
  modelFetch,
  postJsonNoContent,
  putJsonNoContent,
  taxonomyFetch,
} from './modelRegistryFetch';
import type { PrincipalMap } from './principalDisplay';

/** One row of `model_facts`, exactly as `ModelFactRow` serialises it. */
export interface ModelFact {
  model_id: string;
  fact_key: string;
  fact_value: string;
  /** Where the VALUE originated: `registration` | `user` | `mdr_parse`. NOT rendered — see header. */
  source: string;
  /** What a HUMAN did: `unreviewed` | `confirmed` | `edited`. NOT rendered — see header. */
  review_status: string;
  /** The `llm_io_log` record id for a parse-proposed fact; `null` for every other source. */
  llm_io_ref: string | null;
  row_version: number;
  created_at: string;
  updated_at: string;
  /**
   * ➕ **B14 `W-11` / migration `0063` — the Entra oid of whoever last wrote this row.** NOT rendered
   * per row (`R-B`/`R73` keep per-fact chrome off the screen); it is the input to the block-level
   * stamp's actor half, which the server publishes as `last_updated_by`.
   *
   * 🔴 `null` is an HONEST UNKNOWN and it is PERMANENT for rows written before the column existed —
   * `model_facts` write routes emit no audit row, so there is nothing to backfill from. ⛔ Never
   * substitute the registrar or the current owner: `R61`'s *"right once and wrong forever after"*.
   */
  updated_by: string | null;
  /**
   * ⚖️ WI-V3 / ruling `R-Q13` — a READ-PATH PROJECTION of `fact_value` against the published
   * canonical vocabulary. NOT a stored column, and never sent on a write.
   *
   * `'ungoverned'` — no closed set applies to this key (it is one of the four prose keys, or its
   * published block says `governed: false`). `'member'` / `'retired'` / `'off_vocabulary'` — the
   * key IS governed and this is where the stored value sits against it.
   *
   * 🔴 `null` means NOT DETERMINED — the server could not read the vocabulary at all. It is a
   * DIFFERENT fact from `'ungoverned'`, which is a positive statement that nothing governs this
   * key. Render `null` as unknown; rendering it as clean reports an outage as an all-clear.
   *
   * ⚠️ An `'off_vocabulary'` value is NOT an error. The MDR parse records a tier "exactly as the
   * report words it" and is exempt from the vocabulary by construction; this stamp is how such a
   * value stays visible and attributed instead of being coerced or dropped.
   */
  vocabulary_state: 'ungoverned' | 'member' | 'retired' | 'off_vocabulary' | null;
}

/**
 * The canonical block as the API composes it.
 *
 * `last_updated_at` is `null` when there are no facts — the API refuses to stamp an event that did
 * not happen, and the screen must render that absence rather than substituting the model's own
 * creation date.
 *
 * `unreviewed_count` is a COUNT and not a `reviewed` boolean on purpose: "every fact is reviewed" is
 * VACUOUSLY TRUE over an empty set, so a boolean would report a model with no canonicals at all as
 * fully reviewed.
 */
export interface ModelFactsBlock {
  facts: ModelFact[];
  last_updated_at: string | null;
  /**
   * ➕ **B14 `W-11` — the ACTOR on the same stamp**, taken by the server from THE SAME ROW that
   * produced `last_updated_at`. `null` when there are no facts, and also when that row predates
   * migration `0063` — render the unknown-actor string, never a name.
   */
  last_updated_by: string | null;
  /**
   * ➕ **B14 `W-11` — display names for every actor this block names**, mirroring
   * `ModelAccessRoster.principals` exactly (same wire type, same four states, same `may_write`
   * disclosure gate). Read it through `principalDisplay.ts`.
   *
   * ⛔ **THE CANONICALS CARD MUST NOT CALL `resolvePrincipal` FOR THIS.** A per-oid round trip from
   * that card is a SECOND identity-resolution path beside `ModelAccessTab`'s — the failure `W-06`
   * and `W-11` share one wave to avoid.
   *
   * Optional on the type because a payload served by an older API revision has no such key.
   */
  principals?: PrincipalMap;
  unreviewed_count: number;
}

/**
 * The CLOSED set of canonical fact keys, in the locked mock's render order.
 *
 * ⚠️ **A MIRROR OF `Bennie.Api.Models.ModelFactKeys`, AND THE SERVER IS THE AUTHORITY.** It is
 * declared here so the screen can draw the block's field labels without inventing them per component
 * — not so the client can decide what a canonical is. <s>A key the server sends that is not in this
 * list is still RENDERED (see `orderFacts`), because dropping it would hide a fact the registry
 * holds;</s> a key in this list that the server has no row for is simply absent from the block.
 *
 * ➕ **CORRECTED 2026-09-03 (walk item 11, ruling `R-Q36`).** The struck clause was right about
 * ONE of the two ways a key can be missing from this list and wrong about the other, and it was
 * applied to both. It was written for a **stale SWA against a newer `bennie-api`** — a key the
 * server has ADDED and this bundle has not shipped a label for — where dropping the row really
 * would hide a fact the registry holds. It also caught the opposite skew: a key the product has
 * **REMOVED**, whose persisted rows survive the removal. `R-Q36` dropped `materiality`; a model
 * whose facts were written before that still carries one, and the read view rendered it as a bare
 * `materiality` slug because `R-Q38` correctly deleted its labels. The two cases are separated by
 * {@link partitionRenderableFacts}, which asks the SERVER which keys it still declares rather than
 * asking this list alone. ⛔ Do not turn the struck clause into "drop every unrecognised key" —
 * that re-introduces the hiding it was written to prevent, in the skew it was written for.
 *
 * ⛔ `mapped_id` is deliberately NOT here even though the locked mock draws it inside this block. It
 * is §18 item #1 of the FE design lock — a client-asserted alias with its own uniqueness scope and
 * an inbound bulk-sync write surface — and it is unallocated. Rendering it as a canonical would give
 * one identifier two homes.
 */
export const MODEL_FACT_KEYS = [
  'description',
  'business_unit',
  'risk_tier',
  'lifecycle_stage',
  'built_or_bought',
  'regulatory_framework',
] as const;

export type ModelFactKey = (typeof MODEL_FACT_KEYS)[number];

// ⚖️ **B14 `W-06` (2026-09-05) — `NOT_DECLARED_AT_REGISTRATION` IS DELETED, ON BOTH SIDES.**
// It held exactly one key, `model_owner`, and its own note read *"emptying this list is how WI-58
// lands here"*. WI-58 landed as `W-06`: the key left {@link MODEL_FACT_KEYS} outright, so the list
// had nothing left to hold and went with it rather than being emptied. The owner is DERIVED from
// `model_access` now and drawn as a non-input row — see `ModelOverview`'s canonicals card.
// ⛔ The API-side gate that joined the two lists was retired in the same change; what still joins
// the two sides is the DERIVATION check on `REGISTRATION_FACT_KEYS` below.

/**
 * ➕ **WI-62 — the canonicals the registration dialog draws**, in {@link MODEL_FACT_KEYS}'s order.
 *
 * ⛔ **DERIVED, never re-listed.** A fourth hardcoded copy of the key set is a fourth thing to
 * drift, and this file's own header already records what that costs.
 *
 * ⚠️ **The locked mock draws NINE fields here and this list is SHORTER, in two independent ways
 * that are both settled elsewhere.** `mapped_id` is `D7`'s periodic inventory sync rather than a
 * form field and is unallocated (§18 item #1 of the FE design lock), so it is not a
 * {@link MODEL_FACT_KEYS} member at all; `materiality` was removed by WI-69 / ruling `R-Q36`. The
 * mock is authoritative on layout, IA and flow (`R-H`) — the field COUNT is neither.
 */
export const REGISTRATION_FACT_KEYS: readonly ModelFactKey[] = MODEL_FACT_KEYS;


// ═══════════════════════════════════════════════════════════════════════════════════════════════
// ➕ WI-V4 — THE CANONICAL-FACT VOCABULARY, READ FROM THE SERVER
// ═══════════════════════════════════════════════════════════════════════════════════════════════
//
// 🔴 THE ONE SENTENCE THIS SECTION EXISTS TO ENFORCE:
//     THIS CLIENT KNOWS NEITHER WHICH KEYS ARE GOVERNED NOR WHAT THEIR VALUES ARE. BOTH COME OFF
//     THE WIRE, EVERY TIME.
//
// The alternative was measured and refused. Operator ruling `R-Q13` (2026-08-28) priced option C —
// "build the mock's dropdowns with a hardcoded FE enum" — as **measured harmful and per-fact
// IRREVERSIBLE**: `MdrParsePrompt` records a tier *"exactly as the report words it"*, the
// certification walk round-tripped `Tier 1 — high materiality` byte-exact, and no plausible enum
// carries that. Opening the editor over such a fact would COERCE it, and Save would then overwrite
// the owner's fact with a value the client invented — with no fact DELETE and no exposed version
// history to undo it.

/** One member of a governed key's published set. */
export interface ModelFactVocabularyOption {
  value: string;
  label: string;
  sort_order: number;
}

/**
 * The published offer for ONE fact key.
 *
 * 🔴 **`governed` IS THE FACT THIS CLIENT MUST NOT OWN A COPY OF.** `true` ⇒ draw a picker over
 * `values`; `false` ⇒ draw a free-text input, exactly as with no vocabulary at all. Every key in
 * `MODEL_FACT_KEYS` gets an entry, including the four no vocabulary can ever close, so a key's
 * ABSENCE never has to be interpreted — reading absence as "ungoverned" is the collapse the whole
 * server-side design is built to prevent, and it would arrive here where nothing can catch it.
 *
 * ⚠️ **`values` IS THE OFFER, NOT THE VALUE SET.** Retired members are excluded from it by the
 * server and published in `retired_values` instead: they are legal to KEEP and illegal to newly
 * DECLARE. A client that renders only `values` cannot label an owner's stored retired value and
 * will silently drop it. Both lists are EMPTY whenever `governed` is `false`.
 */
export interface ModelFactVocabularyOffer {
  fact_key: string;
  governed: boolean;
  values: ModelFactVocabularyOption[];
  retired_values: ModelFactVocabularyOption[];
}

export interface ModelFactVocabulary {
  fact_vocabularies: ModelFactVocabularyOffer[];
}

/**
 * `GET /api/taxonomy/model-facts` — the deployment's canonical-fact vocabulary.
 *
 * **NOT model-scoped, and not on `/api/models`.** The same four pickers are drawn on the
 * register-a-model dialog, where no model exists yet, so the offer cannot hang off a `{modelId}`.
 * It is CK-published content served beside `/api/taxonomy/severity`, which exists for verbatim the
 * same reason: to stop the frontend hardcoding a vocabulary the curated-knowledge plane owns.
 *
 * ⛔ **THE THREE FAILURE STATES ARE DISTINCT AND NONE OF THEM IS "DRAW A TEXT BOX".** A refusal
 * arrives as a typed `ModelApiError`:
 *   * `model_vocabulary_unavailable` + `error_args.reason === 'artifact_absent'` (500) — nothing is
 *     published for this deployment. A DEPLOY DEFECT; retrying will not fix it.
 *   * `model_vocabulary_unavailable` + `error_args.reason === 'ck_unreachable'` (503) — transient.
 *   * `governed: false` on a key is **NOT** a failure at all: it is a published decision that the
 *     key is deliberately ungoverned, and it arrives on a 200.
 * Treating the first two as the third would draw free-text inputs over keys that may be closed,
 * which looks exactly like it worked. The caller must render them as errors.
 */
export async function getModelFactVocabulary(): Promise<ModelFactVocabulary> {
  const body = await jsonOrThrow<ModelFactVocabulary>(await taxonomyFetch('/model-facts'));
  // Same contract-break discipline as `getModelFacts`, and the same reason: a 200 without the key
  // means this is not the route we think it is, and coercing to `[]` would render "no key is
  // governed" over a read that never landed — a false statement, not a missing one.
  if (!Array.isArray(body?.fact_vocabularies)) {
    throw new Error(
      'MODEL_VOCABULARY_MALFORMED_BODY: GET /api/taxonomy/model-facts answered 200 without a '
      + '`fact_vocabularies` array. Refusing to render an absent vocabulary as an ungoverned one.',
    );
  }
  // 🔴 COMPLETENESS, ENFORCED HERE SO NO CONSUMER HAS TO INTERPRET A GAP. The server publishes an
  // entry for every key; a response missing one leaves a field this client draws with no answer to
  // "is it governed?", and the only two things a caller could do with that are both wrong — draw
  // free text (the absent⇒ungoverned collapse) or drop the field (hide a fact the model holds).
  // Failing at the transport turns an unanswerable question into a loud one, and it buys the editor
  // a hard invariant: every key it renders HAS an offer.
  const published = new Set(body.fact_vocabularies.map((o) => o.fact_key));
  const missing = MODEL_FACT_KEYS.filter((k) => !published.has(k));
  if (missing.length > 0) {
    throw new Error(
      'MODEL_VOCABULARY_INCOMPLETE: GET /api/taxonomy/model-facts published no entry for '
      + `${missing.join(', ')}. Refusing to infer whether those keys are governed.`,
    );
  }
  return body;
}

/**
 * The offer for one key, or `undefined` when the vocabulary carries no entry for it.
 *
 * ⛔ **`undefined` IS NOT "UNGOVERNED".** The server publishes every key, so a gap means the
 * response is not the one this build expects. Callers must treat it as unknown — never as a
 * licence to draw free text.
 */
export function offerFor(
  vocabulary: ModelFactVocabulary | null,
  factKey: string,
): ModelFactVocabularyOffer | undefined {
  return vocabulary?.fact_vocabularies.find((o) => o.fact_key === factKey);
}

/**
 * The model's canonical block — `GET /api/models/{modelId}/facts`.
 *
 * ⚠️ **THIS ROUTE IS AUDITED PER READ (WI-18/WI-20).** Under locked decision L1 registry reads are
 * universal for any authenticated principal, so the compensating control is a `security_audit` row
 * per call rather than a narrower gate. Calling it casually is not free.
 *
 * A missing model is a 404 (`model_not_found`), never an empty block: "no such model" and "this
 * model declared nothing" are opposite facts.
 */
export async function getModelFacts(modelId: string): Promise<ModelFactsBlock> {
  const body = await jsonOrThrow<ModelFactsBlock>(
    await modelFetch(`/${encodeURIComponent(modelId)}/facts`),
  );
  // ⛔ A BODY WITHOUT `facts` IS A CONTRACT BREAK, AND IT FAILS LOUD RATHER THAN COERCING TO `[]`.
  // ⚠️ NOT the same call as `listMdrVersions`' documented `body.mdr_versions ?? []`. There, an
  // absent key genuinely means an empty ledger. Here it cannot: the route composes the block and
  // always emits the key, so its absence means the caller is talking to something that is not this
  // route — an older revision, a proxy, an error page with a 200. Coercing would render "nobody has
  // declared anything" over a read that never landed, which is a false statement about the model
  // rather than a missing one. Thrown rather than returned so the card's `loadError` branch draws
  // the failure it is for; the crash it replaces was a blank page.
  if (!Array.isArray(body?.facts)) {
    throw new Error(
      'MODEL_FACTS_MALFORMED_BODY: GET /api/models/{id}/facts answered 200 without a `facts` array. '
      + 'Refusing to render an absent block as an empty one.',
    );
  }
  return body;
}

/**
 * Create or edit ONE canonical fact — `PUT /api/models/{modelId}/facts/{factKey}`.
 *
 * **`rowVersion` is what makes this a CREATE or an EDIT, and it is not a convenience.** Omit it and
 * the server INSERTs, refusing with `model_fact_already_exists` if the fact is already there. Pass
 * the `row_version` you read and the server EDITs under optimistic concurrency.
 *
 * ⛔ **There is no `source` argument and there must never be one.** An edit KEEPS the row's origin —
 * ruling `R6`: an mdr_parse-proposed fact stays `source='mdr_parse'` with its `llm_io_ref` attached
 * and only its `review_status` flips to `'edited'`. The server decides all three from which door the
 * write came through; a client that could choose would be able to mint a row claiming to be an AI
 * proposal, or strip a real one's provenance.
 *
 * **RETURNS NOTHING** — persist-then-render. The route answers with the refetched block and
 * `frontend-rules` forbids the client drawing it; handing the caller a block invites exactly that
 * render, so it is dropped here and the caller has nothing to draw but a fresh `getModelFacts`.
 *
 * Its refusal vocabulary, all typed through `ModelApiError.code`: `unknown_fact_key`,
 * `fact_value_required`, `model_fact_already_exists`, `model_fact_not_found`,
 * `model_fact_version_conflict`, plus the write gate's `model_archived` / `model_write_forbidden`
 * family.
 *
 * ⚖️ WI-V3 adds three more, on the governable keys only (`risk_tier`, `lifecycle_stage`,
 * `built_or_bought` — THREE since `R-Q36` / WI-69 removed `materiality`, 2026-09-02) and only
 * once a key is armed with `governed: true`:
 * `model_fact_value_not_permitted` and `model_fact_value_retired` (both 400, and both carry
 * `error_args.permitted` naming the legal set), and `model_vocabulary_unavailable` (503 when
 * curated knowledge is unreachable, 500 when the artifact is missing — `error_args.reason` says
 * which). All three have copy in `errors.json` in all three locales. ➕ CORRECTED 2026-09-02:
 * the shipped artifact publishes `governed: true` for every governable key — armed by
 * `941d11c14` and proven on the DEV CK blob — so all three ARE reachable. The prior wording,
 * `"As shipped every key publishes governed: false, so none of them can occur yet"`, was true
 * at drafting and was falsified by that arming.
 */
export async function putModelFact(
  modelId: string, factKey: string, factValue: string, rowVersion?: number,
  llmIoRef?: string,
): Promise<void> {
  const body: { fact_value: string; row_version?: number; llm_io_ref?: string } =
    { fact_value: factValue };
  if (rowVersion !== undefined) body.row_version = rowVersion;
  // ➕ **B14 `W-07` / ruling `R-B3` — THE ACCEPT ARM.** Present ⇒ this save adopts an MDR-parse
  // proposal the person has just read, and the server writes `source='mdr_parse'`,
  // `review_status='confirmed'` with this ref attached. Absent ⇒ an ordinary typed declaration
  // (`source='user'`).
  //
  // ⛔ **OMITTED, NEVER SENT AS AN EMPTY STRING.** `model_facts_mdr_parse_provenance_ck` sees only
  // NOT NULL, so `""` would satisfy it while referring to nothing; the route refuses a present-blank
  // ref by name (`llm_io_ref_blank`) rather than letting it through, and this is the client half of
  // that discipline.
  //
  // ⛔ **THE CALLER MUST ONLY PASS A REF THE PARSE RETURNED, AND ONLY WHEN THE SAVED VALUE IS THE
  // PROPOSED ONE BYTE-FOR-BYTE.** A person who typed over the proposal authored the value
  // themselves, and attributing it to the document would be the provenance falsehood this arm
  // exists to avoid — the caller drops the ref, and the save is an ordinary edit.
  if (llmIoRef !== undefined) body.llm_io_ref = llmIoRef;
  await putJsonNoContent(
    `/${encodeURIComponent(modelId)}/facts/${encodeURIComponent(factKey)}`, body,
  );
}

/**
 * Accept one proposed fact AS-IS — `POST /api/models/{modelId}/facts/{factKey}/confirm`.
 *
 * This is what takes a row OUT of the re-parse target set locked decision **L5** defines. It is a
 * separate route rather than "save the value back to itself" precisely because the latter would flip
 * `review_status` to `'edited'` and record a change nobody made.
 *
 * **RETURNS NOTHING** — same reason as {@link putModelFact}. Refetch.
 */
export async function confirmModelFact(
  modelId: string, factKey: string, rowVersion: number,
): Promise<void> {
  await postJsonNoContent(
    `/${encodeURIComponent(modelId)}/facts/${encodeURIComponent(factKey)}/confirm`,
    { row_version: rowVersion },
  );
}

/**
 * The block's facts in {@link MODEL_FACT_KEYS} order.
 *
 * ⚠️ **THE SERVER ALREADY ORDERS THEM, AND THIS IS NOT A SECOND OPINION — it is a guard against an
 * older serving revision.** `bennie-api` and the SWA are separately deployed, so a client can meet a
 * build whose block predates the ordering. Re-applying a total order the client already declares is
 * idempotent against a correct server and corrective against a stale one; it can never disagree,
 * because both derive from the same list.
 *
 * ⛔ **An UNRECOGNISED key sorts LAST rather than being dropped.** Dropping it would hide a fact the
 * registry holds, and this function is not the place a key-space divergence gets quietly cleaned up.
 * ➕ **UNCHANGED by the 2026-09-03 read-view fix, deliberately.** Sorting and visibility are
 * different questions and they stay in different functions: this one still carries every row it is
 * given, and {@link partitionRenderableFacts} — which CAN see the server's live key space — is the
 * only place a row is held back.
 */
export function orderFacts(facts: ModelFact[]): ModelFact[] {
  const rank = new Map<string, number>(MODEL_FACT_KEYS.map((k, i) => [k, i]));
  return [...facts].sort((a, b) => {
    const ra = rank.get(a.fact_key) ?? Number.MAX_SAFE_INTEGER;
    const rb = rank.get(b.fact_key) ?? Number.MAX_SAFE_INTEGER;
    return ra === rb ? a.fact_key.localeCompare(b.fact_key) : ra - rb;
  });
}

/**
 * ⚖️ Walk item 11 / ruling `R-Q36` (2026-09-03) — split a block's facts into the ones this
 * deployment STILL HAS A FIELD FOR and the ones it does not.
 *
 * <p><b>THE DEFECT THIS EXISTS FOR.</b> `R-Q36` removed `materiality` from the registry's key set
 * and `R-Q38` deleted its three locale entries with it. Neither touches the DATABASE — there is no
 * fact DELETE, and destroying stored rows is not something either ruling authorises. So a model
 * registered before the removal still serves a `materiality` row, and the read view rendered it as
 * a bare `materiality` slug in a `&lt;th&gt;`, which is the raw key `factLabel` prints when no
 * label resolves. `R-Q38`'s parity guard asserts over the KEY SET; it cannot see a persisted row.</p>
 *
 * <p>🔴 <b>THE PREDICATE IS "NEITHER PARTY STILL DECLARES THIS KEY", AND BOTH HALVES ARE
 * LOAD-BEARING.</b> A key missing from {@link MODEL_FACT_KEYS} has TWO possible causes that mean
 * opposite things, and a filter that cannot tell them apart is wrong on one of them:</p>
 * <ul>
 *   <li><b>The server is NEWER</b> — `bennie-api` and the SWA deploy separately, so a key can be
 *       added server-side before this bundle ships a label for it. Hiding that row would hide a
 *       fact the registry holds, which is the case {@link MODEL_FACT_KEYS}' own docblock was
 *       written for. It is RENDERED, under its raw key, exactly as before.</li>
 *   <li><b>The key was REMOVED from the product</b> — the client dropped it and so did the server.
 *       Nothing in the deployment has a field by that name any more, and the row is legacy residue.
 *       It is WITHHELD.</li>
 * </ul>
 *
 * <p><b>The separator comes off the wire, not out of a list in this file.</b>
 * `GET /api/taxonomy/model-facts` publishes <b>one offer per `ModelFactKeys.All`</b> — the offer
 * builder walks `All`, never the published artifact's key set, so the response IS the server's live
 * key space. A removed key is absent from it; a newly-added one is present. ⛔ That is why this
 * takes the vocabulary rather than testing `factKey === 'materiality'`: a name test solves one
 * instance and the next removed field re-opens it, and a name LIST would still be this client
 * deciding what a canonical is — the one thing {@link MODEL_FACT_KEYS}' docblock forbids.</p>
 *
 * <p>⛔ <b>`vocabulary_state` IS NOT THE PREDICATE, AND USING IT WOULD BE A DATA-LOSS BUG.</b> The
 * offending row carries `'ungoverned'`, which is tempting and wrong: `ModelCanonicalVocabulary.Stamp`
 * returns `'ungoverned'` for every key outside `ModelFactKeys.Governable`, and that is THREE of the
 * six live fields — `description`, `business_unit`, `regulatory_framework` (it was four of seven
 * until B14 `W-06` retired `model_owner`). A filter on that stamp would hide half the block on
 * every model in the estate.</p>
 *
 * <p>🔴 <b>A NULL VOCABULARY WITHHOLDS NOTHING.</b> Hiding requires positive evidence that the
 * server no longer declares the key; "we could not read the key space" is not that evidence, and
 * treating it as such would let a CK outage empty a canonical block. This also keeps the read view
 * from WAITING on the vocabulary, which {@link ModelFactsBlock}'s consumer refuses to do on
 * purpose — the block renders complete the moment it arrives, and only becomes more selective once
 * the key space is known.</p>
 */
export function partitionRenderableFacts(
  facts: ModelFact[],
  vocabulary: ModelFactVocabulary | null,
): { rendered: ModelFact[]; withheld: ModelFact[] } {
  if (vocabulary === null) return { rendered: facts, withheld: [] };

  // The UNION, not the server's list alone. A client that is newer than the server would otherwise
  // hide a field it draws in its own editor — the same skew in the other direction.
  const declared = new Set<string>([
    ...MODEL_FACT_KEYS,
    ...vocabulary.fact_vocabularies.map((offer) => offer.fact_key),
  ]);

  const rendered: ModelFact[] = [];
  const withheld: ModelFact[] = [];
  for (const fact of facts) {
    (declared.has(fact.fact_key) ? rendered : withheld).push(fact);
  }
  return { rendered, withheld };
}

/** Re-exported so a caller can `instanceof` without a second import path. */
export { ModelApiError };
